import { LINKEDIN_AUTOMATION_STAGE_STATUSES } from './linkedinAutomationStageStatuses'
import {
  emailDiscoverabilityUrl,
  phoneDiscoverabilityUrl,
} from './linkedinUrls'
import {
  getCurrentValueForRadioButtons,
  saveCurrentValueForRadioButtons,
  toggleRadioButton,
} from './radioButtonUtils'
import { LinkedinSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { LinkedinRecommendationKey, StageType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

const EMAIL_DISCOVERABILITY_SETTINGS = {
  FIRST_DEGREE: '1st degree connections',
  SECOND_DEGREE: '1st and 2nd degree connections',
  ANYONE: 'Anyone',
}

const PHONE_DISCOVERABILITY_SETTINGS = {
  NOBODY: 'Nobody',
  SECOND_DEGREE: '2nd-degree connections',
  EVERYONE: 'Everyone',
}

const TARGET_VALUES = {
  [LinkedinSettingKey.EMAIL_DISCOVERABILITY]:
    EMAIL_DISCOVERABILITY_SETTINGS.FIRST_DEGREE,
  [LinkedinSettingKey.PHONE_DISCOVERABILITY]:
    PHONE_DISCOVERABILITY_SETTINGS.NOBODY,
}

const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === TARGET_VALUES[key])

const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// Automations

enum StageKey {
  LINKEDIN_EMAIL_DISCOVERABILITY_GATHER = 'LINKEDIN_EMAIL_DISCOVERABILITY_GATHER',
  LINKEDIN_PHONE_DISCOVERABILITY_GATHER = 'LINKEDIN_PHONE_DISCOVERABILITY_GATHER',
  LINKEDIN_EMAIL_DISCOVERABILITY_UPDATE = 'LINKEDIN_EMAIL_DISCOVERABILITY_UPDATE',
  LINKEDIN_PHONE_DISCOVERABILITY_UPDATE = 'LINKEDIN_PHONE_DISCOVERABILITY_UPDATE',
  LINKEDIN_EMAIL_DISCOVERABILITY_UNDO = 'LINKEDIN_EMAIL_DISCOVERABILITY_UNDO',
  LINKEDIN_PHONE_DISCOVERABILITY_UNDO = 'LINKEDIN_PHONE_DISCOVERABILITY_UNDO',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_DISCOVERABILITY_GATHER,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: emailDiscoverabilityUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        EMAIL_DISCOVERABILITY_SETTINGS,
        'EMAIL_DISCOVERABILITY',
        LinkedinRecommendationKey.LINKEDIN_PROFILE_DISCOVERABILITY,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_PHONE_DISCOVERABILITY_GATHER,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: phoneDiscoverabilityUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        PHONE_DISCOVERABILITY_SETTINGS,
        'PHONE_DISCOVERABILITY',
        LinkedinRecommendationKey.LINKEDIN_PROFILE_DISCOVERABILITY,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_DISCOVERABILITY_UPDATE,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: emailDiscoverabilityUrl,
    action: async (machineContext, userContext) => {
      const { isDry } = userContext

      const currentSetting = await getCurrentValueForRadioButtons(
        EMAIL_DISCOVERABILITY_SETTINGS,
      )

      if (
        !(
          currentSetting ===
          TARGET_VALUES[LinkedinSettingKey.EMAIL_DISCOVERABILITY]
        ) &&
        !isDry
      ) {
        return toggleRadioButton(EMAIL_DISCOVERABILITY_SETTINGS.FIRST_DEGREE)
      }
      return null
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_PHONE_DISCOVERABILITY_UPDATE,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: phoneDiscoverabilityUrl,
    action: async (machineContext, userContext) => {
      const { isDry } = userContext

      const currentSetting = await getCurrentValueForRadioButtons(
        PHONE_DISCOVERABILITY_SETTINGS,
      )

      if (
        !(
          currentSetting ===
          TARGET_VALUES[LinkedinSettingKey.PHONE_DISCOVERABILITY]
        ) &&
        !isDry
      ) {
        return toggleRadioButton(PHONE_DISCOVERABILITY_SETTINGS.NOBODY)
      }
      return null
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_DISCOVERABILITY_UNDO,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: emailDiscoverabilityUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      const currentSetting = getCurrentValueForRadioButtons(
        EMAIL_DISCOVERABILITY_SETTINGS,
      )

      // Toggle setting if checkbox does not match initial values
      if (initialValues?.EMAIL_DISCOVERABILITY !== currentSetting && !isDry) {
        return toggleRadioButton(initialValues.EMAIL_DISCOVERABILITY)
      }
      return null
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_PHONE_DISCOVERABILITY_UNDO,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: phoneDiscoverabilityUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      const currentSetting = getCurrentValueForRadioButtons(
        PHONE_DISCOVERABILITY_SETTINGS,
      )

      // Toggle setting if checkbox does not match initial values
      if (initialValues?.PHONE_DISCOVERABILITY !== currentSetting && !isDry) {
        return toggleRadioButton(initialValues.PHONE_DISCOVERABILITY)
      }
      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
